import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { changeUserPassword } from "../../../services/user";
import VerticalNavBar from "../../../components/Navbar/PlatformNavBar/VerticalNavBar";
import AccountCircle from "@mui/icons-material/AccountCircle";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import './UserDashboard.css';
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { getUserInfoByEmail } from "../../../services/user";
import { getSubscriptionsByEmail } from "../../../services/stripe";
import { convertUnixTimeMexicoToLocalDate } from "../../../utils/timeConvertor";
import { checkUserMembership, normalizePlanName, getSubscriptionPlanName } from "../../../utils/checkSubscription";

function stringAvatar(name) {
  if (name.split(" ").length > 1) {
    return {
      children: `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
    };
  }
  return {
    children: `${name.split(" ")[0][0]}`,
  };
}

const UserDashboard = () => {

  const reduxUser = useSelector((state) => state.user.currentUser);
  const [userId,setUserId] = useState(reduxUser);
  const userName = userId?.fullName;
  const [errorMessage, setErrorMessage] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { isFetching, error } = useSelector((state) => state.user);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showNewPassword, setNewShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState([]);
  const [isSubscriptionStatusLoading, setIsSubscriptionStatusLoading] = useState(false);

  const dispatch = useDispatch();


  useEffect(() => {
    if (reduxUser) {
      setUserId(reduxUser);
    }
  }, [reduxUser]);

  useEffect(()=>{
    const fetchData = async () => {
      if (!reduxUser?.email) return;
      const fetchedUser = await checkSubscription(reduxUser.email);
      if (fetchedUser) {
        await checkSubscriptionStatus(fetchedUser);
      }
    };
  
    fetchData();

  },[reduxUser?.email])

  const checkSubscription= async(email)=>{
    try {
      setIsLoading(true);
      const res = await getUserInfoByEmail(dispatch,email);
      setUserId(res.data);
      return res.data;
    } catch (error) {
      console.error(error);
      return null;
    }finally{
      setIsLoading(false);
    }
  }

  const getStripePlanName = (subscription) => getSubscriptionPlanName(subscription);

  const checkSubscriptionStatus = async (userData) => {
    setSubscriptionDetails([]);
    setIsSubscriptionStatusLoading(true);

    try {
      if (!userData?.email) {
        setSubscriptionDetails([]);
        return;
      }

      const membershipList = await checkUserMembership(userData);
      const activePlanSet = new Set(
        (membershipList || [])
          .filter((subscriptionStatus) => subscriptionStatus.active)
          .map((subscriptionStatus) => normalizePlanName(subscriptionStatus.subscriptionName))
          .filter(Boolean)
      );

      const fetchedSubscriptions = await getSubscriptionsByEmail(userData.email);
      console.log('Fetched subscriptions:', fetchedSubscriptions);
      const recurringSubscriptions = (fetchedSubscriptions || []).filter((subscription) => {
        const subscriptionStatus = normalizePlanName(subscription?.status);
        if (subscriptionStatus === 'ACTIVE') {
          return true;
        }

        const planName = getStripePlanName(subscription);
        return planName && activePlanSet.has(planName);
      });

      setSubscriptionDetails(recurringSubscriptions);
    } catch (error) {
      console.error('Error fetching subscription details:', error);
      setSubscriptionDetails([]);
    } finally {
      setIsSubscriptionStatusLoading(false);
    }
  };

  const recurringPlanSet = new Set(
    (subscriptionDetails || [])
      .map((subscription) => getStripePlanName(subscription))
      .filter(Boolean)
  );

  const bundleSubscriptions = Array.isArray(userId?.subscriptionPlanId)
    ? userId.subscriptionPlanId.filter((subscription) => !recurringPlanSet.has(normalizePlanName(subscription)))
    : [];

  const recurringSubscriptions = Array.isArray(subscriptionDetails) ? subscriptionDetails : [];

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const openNewTab = () => {
    window.open(
      "https://billing.stripe.com/p/login/9AQ8xu0Nf69SeUU8ww",
      "_blank"
    );
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

  const handleResetPassword = async () => {
    try {
      if (checkMatchPassword()) {
        const response = await changeUserPassword(
          dispatch,
          userId.email,
          password,
          newPassword
        );
        if (response === "Password changed successfully.") {
          setResetPasswordSuccess(true);
          alert("La contraseña se actualizado de forma exitosa");
        } else {
          if (response.request.status === 404) {
            alert("User not found");
            setErrorMessage("Infromación incorrecta");
          } else if (response.request.status === 403) {
            console.log("Password does not match");
            setErrorMessage("Contraseña Incorrecta")
          } else {
            alert("Unexpected error:", response.request.status);
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const checkMatchPassword = () => {
    if (newPassword === confirmPassword) {
      return true;
    } else {
      console.log("PasswordMatch" + newPassword + 'confirmn' + confirmPassword);
      setNewPassword("");
      setConfirmPassword("");
      alert("Las contraseñas no coinciden");
      return false;
    }
  };

  return (
    <section className="index-platform flex">
      <VerticalNavBar />
      <div className="w-full max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* HERO SECTION */}
        <div className="bg-white rounded-[14px] shadow-md overflow-hidden mb-6 animate-fadeUp">
          <div className="hero-stripe"></div>
          <div className="flex items-end gap-4 sm:gap-6 px-4 sm:px-8 pb-6" style={{ marginTop: '-42px' }}>
            <div className="avatar-wrap">
              <div className="custom-avatar">
                {userId?.fullName ? (
                  stringAvatar(userId.fullName).children
                ) : userName ? (
                  stringAvatar(userName).children
                ) : 'U'}
              </div>
              <div className="avatar-badge"></div>
            </div>
            <div className="flex-1 pb-1 z-10">
              <h1 className="text-xl sm:text-2xl font-serif font-semibold text-gray-800 leading-tight">
                {userId?.fullName || userName || 'Usuario'}
              </h1>
              <p className="text-xs sm:text-sm text-gray-400 mt-1">
                {userId?.email || 'email@example.com'}
              </p>
            </div>
          </div>
        </div>

        {/* INFORMACIÓN DE PERFIL */}
        <div className="mb-6">
          <h2 className="text-lg sm:text-xl font-serif font-semibold text-gray-800 mb-4 flex items-center gap-3">
            Información de Perfil
            <span className="flex-1 h-px bg-gray-200"></span>
          </h2>
          
          <div className="user-info-card">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Nombre completo
                </label>
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                  <BadgeIcon className="text-gray-400" fontSize="small" />
                  <span className="text-sm text-gray-800">{userId?.fullName || ''}</span>
                </div>
              </div>
              
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Nombre de usuario
                </label>
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                  <AccountCircle className="text-gray-400" fontSize="small" />
                  <span className="text-sm text-gray-800">{userId?.username || ''}</span>
                </div>
              </div>
              
              <div className="flex flex-col gap-2 sm:col-span-2">
                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Correo electrónico
                </label>
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                  <EmailIcon className="text-gray-400" fontSize="small" />
                  <span className="text-sm text-gray-800">{userId?.email || ''}</span>
                </div>
              </div>
            </div>
            
            <div className="pt-4 border-t border-gray-200">
              <button
                className="inline-flex items-center gap-2 text-sm font-medium text-[#B14A36] hover:text-[#8C0902] transition-colors"
                onClick={() => setChangePassword(!changePassword)}
              >
                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Cambiar contraseña
              </button>
            </div>
          </div>
        </div>
        {/* DETALLES DE SUBSCRIPCIÓN */}
        <div className="mb-6">
          <h2 className="text-lg sm:text-xl font-serif font-semibold text-gray-800 mb-4 flex items-center gap-3">
            Detalles de Subscripción
            <span className="flex-1 h-px bg-gray-200"></span>
          </h2>

          {/* One-time subscriptions */}
          <div className="mb-5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3">
              Compras Unicas (Bundles)
            </h3>
            {bundleSubscriptions.length > 0 ? (
              bundleSubscriptions.map((subscription, index) => (
              <div key={index} className="subscription-row mb-4">
                <div className="subscription-icon">
                  {String(subscription).substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-base font-semibold text-gray-800 truncate">
                    {subscription}
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">
                    Pago único · Sin fecha de expiración
                  </p>
                </div>
                <span className="sub-badge sub-badge-pago shrink-0">Pago único</span>
                <span className="sub-badge sub-badge-active shrink-0">Activa</span>
              </div>
              ))
            ) : (
              <div className="text-sm text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
                No tienes bundles activos por compra unica.
              </div>
            )}
          </div>

          {/* Recurring subscriptions */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3">
              Subscripciones Recurrentes
            </h3>
            {recurringSubscriptions.length > 0 ? (
            recurringSubscriptions.map((item, idx) => {
              if (!item) return null;
              
              // Check if subscription is in trial
              const isInTrial = item.trialStart && item.trialEnd && !item.trialEnd ? false : (item.trialEnd && Date.now() / 1000 < item.trialEnd);
              const subscriptionDisplayName = item.subscriptionName || item.subscriptionType || 'Suscripción';
              
              return (
                <div key={idx} className="subscription-card mb-4">
                  <div className="subscription-row mb-4">
                    <div className="subscription-icon">
                      {subscriptionDisplayName.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif text-base font-semibold text-gray-800 truncate">
                        {subscriptionDisplayName.replace(/_/g, ' ')}
                      </h3>
                      <div className="text-xs text-gray-400 mt-1 space-y-1">
                        {isInTrial ? (
                          <p className="flex items-center gap-1">
                            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                            Periodo de prueba · Expira: {convertUnixTimeMexicoToLocalDate(item.trialEnd)}
                          </p>
                        ) : (
                          <p>
                            Renovación {item.interval === 'month' ? 'mensual' : item.interval === 'year' ? 'anual' : 'automática'} · Expira: {convertUnixTimeMexicoToLocalDate(item.currentPeriodEnd)}
                          </p>
                        )}
                        {item.cancelAtPeriodEnd && (
                          <p className="text-orange-600 font-medium">Se cancelará al final del periodo</p>
                        )}
                      </div>
                    </div>
                    <span className="sub-badge sub-badge-recurrente shrink-0">Recurrente</span>
                    {isInTrial ? (
                      <span className="sub-badge bg-blue-100 text-blue-700 shrink-0">Prueba</span>
                    ) : item.status !== 'active' ? (
                      <span className="sub-badge bg-red-100 text-red-700 shrink-0">Desactivada</span>
                    ) : (
                      <span className="sub-badge sub-badge-active shrink-0">Activa</span>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-3 justify-center pt-3 border-t border-gray-100">
                    <button
                      onClick={openNewTab}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#B14A36] bg-[#FEF9F2] rounded-lg hover:bg-[#FECE79] transition-colors"
                    >
                      <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M19.07 4.93A10 10 0 0 0 4.93 19.07"/>
                      </svg>
                      Gestionar Subscripción
                    </button>
                  </div>
                </div>
              );
            })
          ) : isSubscriptionStatusLoading ? (
            <div className="flex items-center justify-center py-8">
              <CircularProgress size={32} sx={{ color: '#B14A36' }} />
            </div>
          ) : (
            <div className="text-sm text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
              No hay subscripciones recurrentes activas.
            </div>
          )}
          </div>
        </div>

        {/* PASSWORD CHANGE MODAL */}
        {changePassword && (
          <div className="password-modal-backdrop" onClick={(e) => {
            if (e.target === e.currentTarget) setChangePassword(false);
          }}>
            <div className="password-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="password-modal-header">
                <div className="flex items-center gap-4">
                  <div className="password-modal-icon">
                    <svg width="18" height="18" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-serif text-lg font-semibold text-gray-800">
                      Cambiar contraseña
                    </h3>
                    <p className="text-xs text-gray-400 mt-1">
                      Elige una contraseña segura y única
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setChangePassword(false)}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                  aria-label="Cerrar"
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </button>
              </div>

              <div className="password-modal-body">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Contraseña actual
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="custom-input pr-10"
                    />
                    <button
                      type="button"
                      onClick={handleClickShowPassword}
                      className="pwd-toggle-btn"
                      aria-label="Mostrar contraseña"
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Nueva contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      className="custom-input pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setNewShowPassword(!showNewPassword)}
                      className="pwd-toggle-btn"
                      aria-label="Mostrar contraseña"
                    >
                      {showNewPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Confirmar nueva contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="custom-input pr-10"
                    />
                    <button
                      type="button"
                      onClick={handleClickShowConfirmPassword}
                      className="pwd-toggle-btn"
                      aria-label="Mostrar contraseña"
                    >
                      {showConfirmPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </button>
                  </div>
                </div>

                {error && errorMessage && (
                  <Alert severity="error" className="mt-2">
                    {errorMessage}
                  </Alert>
                )}
              </div>

              <div className="password-modal-footer">
                <button
                  onClick={() => setChangePassword(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-transparent border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-400 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={isFetching}
                  className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isFetching ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <>
                      <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      Actualizar contraseña
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default UserDashboard;
